import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { Button } from "components/ui/button";
import RootlyFullLogo from "components/ui/icons/rootly_logo";
import TierZeroMark from "components/ui/icons/tierzero_mark";
import { Input } from "components/ui/input";
import { ShieldCheck } from "lucide-react";
import React, { useCallback, useState } from "react";
import {
  getBrandThemeFromDomain,
  getSupportEmail,
  isRootlyBranded,
} from "src/utils/brandTheme";

type VerifyMfaPageProps = {
  onVerified: () => void;
};

const VerifyMfaPage: React.FC<VerifyMfaPageProps> = ({ onVerified }) => {
  const { verifyMfaForLogin, verifyMfaBackupCodeForLogin } = useAuthFrontendApis();
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const brandTheme = getBrandThemeFromDomain();
  const supportEmail = getSupportEmail(brandTheme);
  const isRootly = isRootlyBranded(brandTheme);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = code.trim();
      if (!trimmed) {
        setError("Please enter your code.");
        return;
      }

      setSubmitting(true);
      setError("");

      try {
        if (useBackupCode) {
          const response = await verifyMfaBackupCodeForLogin({ code: trimmed });
          await response.handle({
            success() {
              onVerified();
            },
            invalidCode(err) {
              setError(err.user_facing_error || "That backup code is not valid.");
            },
            mfaCookieTimeout(err) {
              setError(err.user_facing_error || "Your session has timed out. Please log in again.");
            },
            accountDisabled(err) {
              setError(err.user_facing_error || `Your account has been disabled. Please reach out to ${supportEmail}.`);
            },
            accountLocked(err) {
              setError(err.user_facing_error || `Your account has been locked. Please reach out to ${supportEmail}.`);
            },
            unexpectedOrUnhandled() {
              setError(`Something went wrong. Please reach out to ${supportEmail}.`);
            },
          });
        } else {
          const response = await verifyMfaForLogin({ code: trimmed });
          await response.handle({
            success() {
              onVerified();
            },
            badRequest(err) {
              setError(err.user_facing_errors?.code || "That code is not valid.");
            },
            mfaCookieTimeout(err) {
              setError(err.user_facing_error || "Your session has timed out. Please log in again.");
            },
            accountDisabled(err) {
              setError(err.user_facing_error || `Your account has been disabled. Please reach out to ${supportEmail}.`);
            },
            accountLocked(err) {
              setError(err.user_facing_error || `Your account has been locked. Please reach out to ${supportEmail}.`);
            },
            unexpectedOrUnhandled() {
              setError(`Something went wrong. Please reach out to ${supportEmail}.`);
            },
          });
        }
      } catch {
        setError(`Something went wrong. Please reach out to ${supportEmail}.`);
      } finally {
        setSubmitting(false);
      }
    },
    [code, useBackupCode, verifyMfaForLogin, verifyMfaBackupCodeForLogin, onVerified, supportEmail],
  );

  const toggleBackup = useCallback(() => {
    setUseBackupCode((v) => !v);
    setCode("");
    setError("");
  }, []);

  return (
    <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          {isRootly ? (
            <RootlyFullLogo className="h-10 w-auto" />
          ) : (
            <TierZeroMark className="h-14 w-auto text-[#3B82F6]" />
          )}
        </div>

        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#3B82F6]/20">
            <ShieldCheck className="w-6 h-6 text-[#3B82F6]" />
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-2 text-center">
          Two-factor authentication
        </h1>
        <p className="text-neutral-400 text-center mb-8">
          {useBackupCode
            ? "Enter one of your backup codes to continue."
            : "Enter the code from your authenticator app to continue."}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="rounded-xl border border-neutral-700/60 bg-neutral-900/50 p-6">
            <Input
              type="text"
              inputMode={useBackupCode ? "text" : "numeric"}
              autoComplete="one-time-code"
              placeholder={useBackupCode ? "Backup code" : "123456"}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (error) setError("");
              }}
              className="w-full bg-neutral-800/80 border-neutral-700 text-white placeholder:text-neutral-500 h-12 text-base tracking-widest text-center"
              autoFocus
              disabled={submitting}
            />
            {error && (
              <p className="text-red-400 text-sm mt-3">{error}</p>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full mt-4 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium"
            loading={submitting}
            disabled={submitting || !code.trim()}
          >
            Verify
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={toggleBackup}
            className="text-[#3B82F6] hover:text-[#3B82F6]/80 text-sm underline underline-offset-2 transition-colors"
            disabled={submitting}
          >
            {useBackupCode ? "Use authenticator code instead" : "Use a backup code instead"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyMfaPage;
